import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TerminologyModal.css';
import { FaPlus, FaTrash, FaTimes, FaFileDownload, FaHistory, FaFileCsv } from 'react-icons/fa';
import { useClickOutside } from './hooks';
import { fetchHistory, fetchTerminologyDict } from './api';
import HistoryDropdown from './HistoryDropdown';
import {
  buildCsvTemplateContent,
  CSV_PAGE_SIZE,
  MAX_CSV_ROWS,
  paginateEntries,
  parseAndValidateTerminologyCsv,
} from './csvUtils';

const TerminologyModal = ({ show, onClose, onSave, formId }) => {
  const [entries, setEntries] = useState([]);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [historyTasks, setHistoryTasks] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCsvImporting, setIsCsvImporting] = useState(false);
  const [showCsvDropdown, setShowCsvDropdown] = useState(false);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvWarnings, setCsvWarnings] = useState([]);
  const [csvSummary, setCsvSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const historyDropdownRef = useRef(null);
  const importButtonRef = useRef(null);
  const csvDropdownRef = useRef(null);
  const csvButtonRef = useRef(null);
  const csvInputRef = useRef(null);

  useClickOutside(historyDropdownRef, importButtonRef, () => setShowHistory(false));
  useClickOutside(csvDropdownRef, csvButtonRef, () => setShowCsvDropdown(false));

  const pagedEntries = useMemo(
    () => paginateEntries(entries, currentPage, CSV_PAGE_SIZE),
    [entries, currentPage]
  );

  useEffect(() => {
    if (currentPage !== pagedEntries.page) {
      setCurrentPage(pagedEntries.page);
    }
  }, [currentPage, pagedEntries.page]);

  const clearCsvFeedback = () => {
    setCsvErrors([]);
    setCsvWarnings([]);
    setCsvSummary(null);
  };

  const addEntry = () => {
    if (key && value) {
      const newEntries = [...entries, { key, value }];
      setEntries(newEntries);
      setKey('');
      setValue('');
      clearCsvFeedback();
    }
  };

  const removeEntry = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    clearCsvFeedback();
  };

  const clearAll = () => {
    setEntries([]);
    setCurrentPage(1);
    clearCsvFeedback();
  };

  const importFromHistory = async () => {
    setShowCsvDropdown(false);
    setLoading(true);
    try {
      const tasks = await fetchHistory();
      setHistoryTasks(tasks);
      setShowHistory(true);
      clearCsvFeedback();
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHistorySelection = async (uuid) => {
    setLoading(true);
    try {
      const newEntries = await fetchTerminologyDict(uuid);
      setEntries(newEntries);
      setShowHistory(false);
      setCurrentPage(1);
      clearCsvFeedback();
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCsvClick = () => {
    setShowCsvDropdown(false);
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
      csvInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    setShowCsvDropdown(false);
    const blob = new Blob([buildCsvTemplateContent()], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'terminology_template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsCsvImporting(true);
    setShowHistory(false);
    try {
      const csvText = await file.text();
      const result = parseAndValidateTerminologyCsv(csvText, { maxRows: MAX_CSV_ROWS });
      setCsvErrors(result.errors);
      setCsvWarnings(result.warnings);
      setCsvSummary(result.summary);

      if (!result.ok) {
        return;
      }

      setEntries(result.entries);
      setCurrentPage(1);
    } catch (err) {
      setCsvErrors([{ row: '-', message: err.message || 'Failed to import CSV.' }]);
      setCsvWarnings([]);
      setCsvSummary(null);
    } finally {
      setIsCsvImporting(false);
    }
  };

  const handleConfirm = () => {
    if (key && value) {
      onSave([...entries, { key, value }]);
    } else {
      onSave(entries);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>Create Project Dictionary</h2>
          <button onClick={onClose} className="close-button"><FaTimes /></button>
        </div>
        <p className="modal-description">By telling us more specific translation mappings, we can help you check more.
        </p>
        <p className="modal-description">For example: <u><i>"technician"</i></u> is usually translated as "技师、技术人员", but it is translated as <u><i></i>"工程师"</u> in this project.
        </p>
        <div className="modal-content">
          <div className="entry-form">
            <input
              type="text"
              placeholder="English, e.g.: technician centers"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <input
              type="text"
              placeholder="Chinese, e.g.: 工程师驿站"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <button onClick={addEntry}><FaPlus /> Add</button>
          </div>
          <div className="entry-list">
            {csvSummary && (
              <div className={`csv-summary ${csvErrors.length > 0 ? 'csv-summary-error' : 'csv-summary-success'}`}>
                <strong>CSV Summary:</strong> {csvSummary.validRows}/{csvSummary.totalRows} rows imported.
                {csvSummary.errorCount > 0 && (
                  <> Errors: {csvSummary.errorCount}</>
                )}
                {csvSummary.duplicateKeys > 0 && (
                  <> Duplicates ignored: {csvSummary.duplicateKeys}</>
                )}
              </div>
            )}
            {csvWarnings.length > 0 && (
              <div className="csv-feedback csv-feedback-warning">
                <strong>Warnings</strong>
                {csvWarnings.slice(0, 10).map((warning, index) => (
                  <p key={`${warning.row}-${index}`}>Row {warning.row}: {warning.message}</p>
                ))}
              </div>
            )}
            {csvErrors.length > 0 && (
              <div className="csv-feedback csv-feedback-error">
                <strong>Errors</strong>
                {csvErrors.map((error, index) => (
                  <p key={`${error.row}-${index}`}>Row {error.row}: {error.message}</p>
                ))}
                {csvSummary?.hiddenErrorCount > 0 && (
                  <p>...and {csvSummary.hiddenErrorCount} more errors.</p>
                )}
              </div>
            )}
            {pagedEntries.items.map((entry, index) => (
              <div key={index} className="entry-item">
                <span title={entry.key}>{entry.key}</span>
                <span title={entry.value}>{entry.value}</span>
                <button
                  className="close-button"
                  onClick={() => removeEntry((pagedEntries.page - 1) * pagedEntries.pageSize + index)}
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            {pagedEntries.totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={pagedEntries.page <= 1}
                >
                  Prev
                </button>
                <span>
                  Page {pagedEntries.page} / {pagedEntries.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagedEntries.totalPages))}
                  disabled={pagedEntries.page >= pagedEntries.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <div className="footer-button-group">
            <button ref={importButtonRef} onClick={importFromHistory} className="import-history-button" disabled={loading || isCsvImporting}>
              {loading ? 'Loading...' : <><FaHistory /> Import From History</>}
            </button>
            <button 
              ref={csvButtonRef} 
              onClick={() => {
                setShowHistory(false);
                setShowCsvDropdown(!showCsvDropdown);
              }} 
              className="csv-options-button" 
              disabled={isCsvImporting || loading}
            >
              {isCsvImporting ? 'Importing...' : <><FaFileCsv /> Import From Excel</>}
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvFileChange}
              className="csv-file-input"
            />
            {showHistory && (
              <HistoryDropdown
                tasks={historyTasks}
                onSelect={handleHistorySelection}
                dropdownRef={historyDropdownRef}
              />
            )}
            {showCsvDropdown && (
              <div ref={csvDropdownRef} className="csv-dropdown">
                <ul>
                  <li onClick={handleDownloadTemplate}>
                    <FaFileDownload style={{ marginRight: '8px' }} /> Download Template
                  </li>
                  <li onClick={handleImportCsvClick}>
                    <FaFileCsv style={{ marginRight: '8px' }} /> Import (CSV)
                  </li>
                </ul>
              </div>
            )}
          </div>
          <div className="footer-button-group">
            <button onClick={handleConfirm}><FaFileDownload /> Confirm</button>
            <button onClick={clearAll} className="clear-all-button"><FaTrash /> Clear All</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminologyModal;
